import { Link } from "react-router-dom";
import { useLocalization } from "@/context/localization-context";
import { Ago, EmptyBlock, LevelBadge, SectionCard, SeverityBadge } from "../../components/primitives";
import { formatStamp } from "../../format";
import type { BranchMeta, PipelineSection } from "../../types";
import { Chip, PizzaRef } from "./shared";

type BranchOf = (id: string | null | undefined) => BranchMeta | null;

/** Pizzas photographed more than 3 min ago without a verdict, with where they got stuck. */
export function StuckCard({ data, branchOf }: { data: PipelineSection; branchOf: BranchOf }) {
  const { t, tp } = useLocalization();
  const name = (id: string | null) => branchOf(id)?.name || id || t("monitoring.unknownBranch");

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          {t("monitoring.pipeline.stuck.title")}
          {data.missing_total > 0 && <SeverityBadge severity="critical" label={data.missing_total} />}
        </span>
      }
      description={t("monitoring.pipeline.stuck.description")}
      contentClassName="p-0"
    >
      {data.missing.length === 0 ? (
        <div className="p-4">
          <EmptyBlock>{t("monitoring.pipeline.stuck.empty")}</EmptyBlock>
        </div>
      ) : (
        <>
          <ul className="max-h-[28rem] divide-y divide-border/60 overflow-y-auto">
            {data.missing.map((pizza) => (
              <li key={pizza.id} className="flex flex-col gap-1.5 px-4 py-2.5">
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                  <PizzaRef id={pizza.id} name={pizza.name} className="flex-1" />
                  <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
                    <Ago ts={pizza.capture_at} />
                    <span className="text-[11px] tabular-nums text-muted-foreground">{formatStamp(pizza.capture_at)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                  <span className="break-words text-muted-foreground">{name(pizza.branch_id)}</span>
                  {pizza.upload_received ? (
                    <SeverityBadge severity="warning" label={t("monitoring.pipeline.stuck.photoYes")} />
                  ) : (
                    <SeverityBadge severity="critical" label={t("monitoring.pipeline.stuck.photoNo")} />
                  )}
                  <span className="text-[11px] text-muted-foreground">
                    {pizza.upload_received ? t("monitoring.pipeline.stuck.photoYesHint") : t("monitoring.pipeline.stuck.photoNoHint")}
                  </span>
                  {pizza.capture_count > 1 && <Chip>{tp("monitoring.pipeline.captureCount", pizza.capture_count)}</Chip>}
                  {pizza.order_id && (
                    <Link to={`/orders/${pizza.order_id}`} className="text-[11px] text-muted-foreground hover:text-foreground hover:underline">
                      {t("monitoring.pipeline.order")}
                    </Link>
                  )}
                </div>
                {pizza.last_event && (
                  <div className="flex min-w-0 items-start gap-2 rounded-md bg-muted/30 px-2 py-1.5">
                    <LevelBadge level={pizza.last_event.level} />
                    <div className="min-w-0 flex-1">
                      <div className="break-words text-xs leading-snug">{pizza.last_event.message}</div>
                      <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-muted-foreground">
                        <span className="font-mono">
                          {pizza.last_event.source} · {pizza.last_event.code}
                        </span>
                        <Ago ts={pizza.last_event.ts} />
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {data.missing_total > data.missing.length && (
            <p className="border-t px-4 py-2 text-[11px] text-muted-foreground">
              {t("monitoring.pipeline.more", { shown: data.missing.length, total: data.missing_total })}
            </p>
          )}
        </>
      )}
    </SectionCard>
  );
}

/** Pizzas packed more than 5 min ago at a branch that photographs, never captured (last 6 h at most). */
export function UncapturedCard({ data, branchOf }: { data: PipelineSection; branchOf: BranchOf }) {
  const { t } = useLocalization();
  const list = data.funnel.uncaptured;
  const total = data.funnel.totals.uncaptured;
  const name = (id: string | null) => branchOf(id)?.name || id || t("monitoring.unknownBranch");
  // at app-trigger branches a missing photo is the staff's habit; red only when the camera should have fired
  const severity = list.some((p) => p.bb_trigger) || list.length === 0 ? "critical" : "warning";

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          {t("monitoring.pipeline.uncaptured.title")}
          {total > 0 && <SeverityBadge severity={severity} label={total} />}
        </span>
      }
      description={t("monitoring.pipeline.uncaptured.description")}
      contentClassName="p-0"
    >
      {list.length === 0 ? (
        <div className="p-4">
          <EmptyBlock>{t("monitoring.pipeline.uncaptured.empty")}</EmptyBlock>
        </div>
      ) : (
        <>
          <ul className="max-h-[28rem] divide-y divide-border/60 overflow-y-auto">
            {list.map((pizza) => (
              <li key={pizza.id} className="flex flex-col gap-1.5 px-4 py-2.5">
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                  <PizzaRef id={pizza.id} name={pizza.name} className="flex-1" />
                  <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
                    <span>
                      <span className="text-muted-foreground">{t("monitoring.pipeline.uncaptured.packed")} </span>
                      <Ago ts={pizza.packed_at} />
                    </span>
                    <span className="text-[11px] tabular-nums text-muted-foreground">{formatStamp(pizza.packed_at)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                  <span className="break-words text-muted-foreground">{name(pizza.branch_id)}</span>
                  {!pizza.bb_trigger && (
                    <Chip tone="warn" title={t("monitoring.cameras.flag.manualTriggerHint")}>
                      {t("monitoring.pipeline.uncaptured.manual")}
                    </Chip>
                  )}
                  {pizza.order_id && (
                    <Link to={`/orders/${pizza.order_id}`} className="break-all text-[11px] text-muted-foreground hover:text-foreground hover:underline">
                      {t("monitoring.pipeline.order")} <span className="font-mono">{pizza.order_id}</span>
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {total > list.length && (
            <p className="border-t px-4 py-2 text-[11px] text-muted-foreground">
              {t("monitoring.pipeline.more", { shown: list.length, total })}
            </p>
          )}
        </>
      )}
    </SectionCard>
  );
}
